import { useEffect, useState } from "react";
import axios from "axios";
import { useLang } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * DevelopmentSection — structured coach feedback sessions
 *  - mode="admin": create/edit/delete sessions, pick before/after videos
 *  - mode="parent": read-only view with Before/After comparison
 *
 * Props:
 *  - playerId (admin) — player id
 *  - token (parent) — access token
 *  - fileUrlFor(videoId) — how to stream a video
 *  - authHeaders — admin headers
 *  - videos — list of videos already loaded (needed for before/after selectors)
 */
export default function DevelopmentSection({
  mode,
  playerId,
  token,
  fileUrlFor,
  authHeaders = {},
  videos = [],
}) {
  const { t } = useLang();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // null | 'new' | session object
  const [busy, setBusy] = useState(false);

  const listUrl =
    mode === "admin"
      ? `${API}/admin/players/${playerId}/dev-sessions`
      : `${API}/p/${token}/dev-sessions`;

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const r = await axios.get(listUrl, { headers: authHeaders });
      setSessions(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [playerId, token]);

  const saveSession = async (data) => {
    setBusy(true);
    try {
      if (editing === "new") {
        await axios.post(
          `${API}/admin/players/${playerId}/dev-sessions`,
          data,
          { headers: authHeaders }
        );
      } else {
        await axios.patch(
          `${API}/admin/dev-sessions/${editing.id}`,
          data,
          { headers: authHeaders }
        );
      }
      setEditing(null);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  const deleteSession = async (id) => {
    if (!window.confirm(t("dev_delete_confirm"))) return;
    await axios.delete(`${API}/admin/dev-sessions/${id}`, { headers: authHeaders });
    await load();
  };

  if (loading) {
    return <div className="text-neutral-500 p-10 text-center">...</div>;
  }
  if (error) {
    return (
      <div className="rounded-xl bg-[#e05555]/10 border border-[#e05555]/40 text-[#e05555] p-4">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="development-section">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-bebas text-3xl tracking-widest text-white">
            💡 {t("development_title")}
          </h2>
          <p className="text-xs text-neutral-400 mt-1">
            {t("development_subtitle")}
          </p>
        </div>
        {mode === "admin" && (
          <button
            onClick={() => setEditing("new")}
            data-testid="dev-add-session-btn"
            className="px-4 py-2 rounded-lg bg-[#e8559a] hover:bg-[#c9407f] text-white font-bebas tracking-widest text-sm transition-colors"
          >
            ＋ {t("dev_new_session")}
          </button>
        )}
      </div>

      {/* Editor modal */}
      {editing && mode === "admin" && (
        <SessionEditor
          initial={editing === "new" ? null : editing}
          videos={videos}
          onCancel={() => setEditing(null)}
          onSave={saveSession}
          busy={busy}
          t={t}
        />
      )}

      {/* List */}
      {sessions.length === 0 ? (
        <div className="rounded-2xl bg-[#161616] border border-[#252525] p-10 text-center">
          <div className="text-5xl mb-3">🎯</div>
          <div className="text-neutral-400 mb-1">{t("dev_no_sessions")}</div>
          {mode === "admin" && (
            <div className="text-xs text-neutral-600">{t("dev_add_first_session")}</div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {sessions.map((s, idx) => (
            <SessionCard
              key={s.id}
              session={s}
              videos={videos}
              fileUrlFor={fileUrlFor}
              authHeaders={authHeaders}
              mode={mode}
              t={t}
              onEdit={() => setEditing(s)}
              onDelete={() => deleteSession(s.id)}
              index={sessions.length - idx}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ============ Session Card ============
function SessionCard({
  session: s,
  videos,
  fileUrlFor,
  authHeaders,
  mode,
  t,
  onEdit,
  onDelete,
  index,
}) {
  const before = videos.find((v) => v.id === s.before_video_id);
  const after = videos.find((v) => v.id === s.after_video_id);
  const hasComparison = before || after;

  return (
    <div
      data-testid={`dev-session-${s.id}`}
      className="rounded-2xl bg-[#161616] border border-[#252525] overflow-hidden"
    >
      {/* Header */}
      <div className="p-5 sm:p-6 border-b border-[#252525]">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3 min-w-0">
            <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[#e8559a]/10 border border-[#e8559a]/40 flex items-center justify-center">
              <span className="font-bebas text-xl text-[#e8559a] leading-none">
                #{index}
              </span>
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#e8559a]/80 mb-0.5">
                {t("session_from")} {s.session_date}
              </div>
              <h3 className="font-bebas text-2xl tracking-widest text-white leading-tight">
                {s.skill_worked}
              </h3>
              <div className="text-sm text-neutral-300 mt-1">
                🎯 <span className="text-neutral-400">{t("dev_objective")}:</span>{" "}
                {s.objective}
              </div>
            </div>
          </div>
          {mode === "admin" && (
            <div className="flex gap-2">
              <button
                onClick={onEdit}
                data-testid={`dev-edit-${s.id}`}
                className="px-3 py-1.5 rounded-md bg-[#0f0f0f] border border-[#252525] hover:border-[#e8559a]/50 text-xs text-neutral-200 transition-colors"
              >
                ✎ {t("edit")}
              </button>
              <button
                onClick={onDelete}
                data-testid={`dev-delete-${s.id}`}
                className="px-3 py-1.5 rounded-md bg-[#e05555]/10 border border-[#e05555]/40 hover:bg-[#e05555]/20 text-xs text-[#e05555] transition-colors"
              >
                🗑
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Before / After videos */}
      {hasComparison && (
        <div className="p-5 sm:p-6 border-b border-[#252525] bg-[#0f0f0f]/40">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#e8559a]/80 mb-3">
            🎬 {t("dev_before_after_title")}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <BeforeAfterSlot
              label={`◀ ${t("dev_before_video")}`}
              video={before}
              fileUrlFor={fileUrlFor}
              authHeaders={authHeaders}
              accent="border-amber-500/40 bg-amber-500/5"
              t={t}
            />
            <BeforeAfterSlot
              label={`▶ ${t("dev_after_video")}`}
              video={after}
              fileUrlFor={fileUrlFor}
              authHeaders={authHeaders}
              accent="border-emerald-500/40 bg-emerald-500/5"
              t={t}
            />
          </div>
        </div>
      )}

      {/* Progress fields */}
      <div className="p-5 sm:p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <FieldBlock
          emoji="✅"
          label={t("dev_what_improved")}
          text={s.what_improved}
          accent="text-emerald-400 border-emerald-500/30 bg-emerald-500/5"
        />
        <FieldBlock
          emoji="🎯"
          label={t("dev_what_needs_work")}
          text={s.what_needs_work}
          accent="text-amber-300 border-amber-500/30 bg-amber-500/5"
        />
        <FieldBlock
          emoji="📚"
          label={t("dev_homework")}
          text={s.homework}
          accent="text-sky-300 border-sky-500/30 bg-sky-500/5"
        />
        <FieldBlock
          emoji="🚀"
          label={t("dev_next_objective")}
          text={s.next_objective}
          accent="text-[#e8559a] border-[#e8559a]/30 bg-[#e8559a]/5"
        />
        {mode === "admin" && s.coach_notes && (
          <div className="md:col-span-2">
            <FieldBlock
              emoji="🔒"
              label={t("dev_coach_notes")}
              text={s.coach_notes}
              accent="text-neutral-300 border-[#252525] bg-[#0f0f0f]"
            />
          </div>
        )}
      </div>
    </div>
  );
}

function FieldBlock({ emoji, label, text, accent }) {
  if (!text) return null;
  return (
    <div className={`rounded-xl border p-3.5 ${accent}`}>
      <div className="text-[10px] uppercase tracking-[0.25em] mb-1.5 opacity-80">
        {emoji} {label}
      </div>
      <div className="text-sm text-white leading-relaxed whitespace-pre-wrap">
        {text}
      </div>
    </div>
  );
}

function BeforeAfterSlot({ label, video, fileUrlFor, authHeaders, accent, t }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (blobUrl || !video) return;
    setLoading(true);
    try {
      const r = await axios.get(fileUrlFor(video.id), {
        headers: authHeaders,
        responseType: "blob",
      });
      const u = URL.createObjectURL(r.data);
      setBlobUrl(u);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  if (!video) {
    return (
      <div className={`rounded-lg border ${accent} p-4 aspect-video flex items-center justify-center text-neutral-500 text-sm`}>
        {label} · —
      </div>
    );
  }
  return (
    <div className={`rounded-lg border ${accent} p-2`}>
      <div className="text-[10px] uppercase tracking-[0.25em] mb-2 opacity-90 px-1">
        {label} · {video.title}
      </div>
      {blobUrl ? (
        <video
          src={blobUrl}
          controls
          className="w-full aspect-video rounded-md bg-black"
        />
      ) : (
        <button
          type="button"
          onClick={load}
          className="w-full aspect-video rounded-md bg-[#0a0a0a] border border-[#252525] flex items-center justify-center text-neutral-300 hover:border-[#e8559a]/50 transition-colors text-sm"
        >
          {loading ? "..." : `▶ ${video.title}`}
        </button>
      )}
    </div>
  );
}

// ============ Session Editor (admin) ============
function SessionEditor({ initial, videos, onCancel, onSave, busy, t }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    session_date: initial?.session_date || today,
    skill_worked: initial?.skill_worked || "",
    objective: initial?.objective || "",
    before_video_id: initial?.before_video_id || "",
    after_video_id: initial?.after_video_id || "",
    what_improved: initial?.what_improved || "",
    what_needs_work: initial?.what_needs_work || "",
    homework: initial?.homework || "",
    next_objective: initial?.next_objective || "",
    coach_notes: initial?.coach_notes || "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = (e) => {
    e.preventDefault();
    if (!form.skill_worked.trim() || !form.objective.trim()) return;
    const payload = { ...form };
    if (!payload.before_video_id) payload.before_video_id = null;
    if (!payload.after_video_id) payload.after_video_id = null;
    onSave(payload);
  };

  const inputCls =
    "w-full bg-[#0f0f0f] border border-[#252525] rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#e8559a] focus:outline-none";

  return (
    <form
      onSubmit={submit}
      data-testid="dev-session-editor"
      className="rounded-2xl bg-[#161616] border border-[#e8559a]/40 p-5 sm:p-6 space-y-4"
    >
      <div className="flex items-center justify-between">
        <h3 className="font-bebas text-2xl tracking-widest text-[#e8559a]">
          {initial ? `✎ ${t("dev_edit_session")}` : `＋ ${t("dev_new_session")}`}
        </h3>
        <button
          type="button"
          onClick={onCancel}
          className="text-neutral-400 hover:text-white text-xl leading-none"
        >
          ✕
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1.5">
            {t("training_date")} *
          </label>
          <input
            type="date"
            required
            data-testid="dev-editor-date"
            className={inputCls}
            value={form.session_date}
            onChange={(e) => set("session_date", e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1.5">
            {t("dev_skill_worked")} *
          </label>
          <input
            required
            data-testid="dev-editor-skill"
            className={inputCls}
            placeholder={t("dev_skill_placeholder")}
            value={form.skill_worked}
            onChange={(e) => set("skill_worked", e.target.value)}
          />
        </div>
      </div>

      <div>
        <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1.5">
          {t("dev_objective")} *
        </label>
        <input
          required
          data-testid="dev-editor-objective"
          className={inputCls}
          placeholder={t("dev_objective_placeholder")}
          value={form.objective}
          onChange={(e) => set("objective", e.target.value)}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1.5">
            ◀ {t("dev_before_video")}
          </label>
          <select
            data-testid="dev-editor-before"
            className={inputCls}
            value={form.before_video_id || ""}
            onChange={(e) => set("before_video_id", e.target.value)}
          >
            <option value="">{t("dev_none")}</option>
            {videos.map((v) => (
              <option key={v.id} value={v.id}>
                {v.title} {v.session_date ? `· ${v.session_date}` : ""}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1.5">
            ▶ {t("dev_after_video")}
          </label>
          <select
            data-testid="dev-editor-after"
            className={inputCls}
            value={form.after_video_id || ""}
            onChange={(e) => set("after_video_id", e.target.value)}
          >
            <option value="">{t("dev_none")}</option>
            {videos.map((v) => (
              <option key={v.id} value={v.id}>
                {v.title} {v.session_date ? `· ${v.session_date}` : ""}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <TextArea
          label={`✅ ${t("dev_what_improved")}`}
          value={form.what_improved}
          onChange={(v) => set("what_improved", v)}
          placeholder={t("dev_what_improved_placeholder")}
          testid="dev-editor-improved"
        />
        <TextArea
          label={`🎯 ${t("dev_what_needs_work")}`}
          value={form.what_needs_work}
          onChange={(v) => set("what_needs_work", v)}
          placeholder={t("dev_what_needs_work_placeholder")}
          testid="dev-editor-needswork"
        />
        <TextArea
          label={`📚 ${t("dev_homework")}`}
          value={form.homework}
          onChange={(v) => set("homework", v)}
          placeholder={t("dev_homework_placeholder")}
          testid="dev-editor-homework"
        />
        <TextArea
          label={`🚀 ${t("dev_next_objective")}`}
          value={form.next_objective}
          onChange={(v) => set("next_objective", v)}
          placeholder={t("dev_next_objective_placeholder")}
          testid="dev-editor-next"
        />
      </div>

      <TextArea
        label={`🔒 ${t("dev_coach_notes")}`}
        value={form.coach_notes}
        onChange={(v) => set("coach_notes", v)}
        placeholder="—"
        testid="dev-editor-coach-notes"
        rows={2}
      />

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-lg bg-[#0f0f0f] border border-[#252525] hover:border-neutral-500 text-neutral-300 text-sm transition-colors"
        >
          {t("cancel")}
        </button>
        <button
          type="submit"
          disabled={busy}
          data-testid="dev-editor-save"
          className="px-5 py-2 rounded-lg bg-[#e8559a] hover:bg-[#c9407f] disabled:opacity-60 text-white font-bebas tracking-widest text-sm transition-colors"
        >
          {busy ? "..." : `💾 ${t("dev_save_session")}`}
        </button>
      </div>
    </form>
  );
}

function TextArea({ label, value, onChange, placeholder, testid, rows = 3 }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1.5">
        {label}
      </label>
      <textarea
        rows={rows}
        data-testid={testid}
        className="w-full bg-[#0f0f0f] border border-[#252525] rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#e8559a] focus:outline-none resize-none"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
